import Loading, { LoadingBall } from '@ingka/loading';
import SSRIcon from '@ingka/ssr-icon';
import arrowCloudIn from '@ingka/ssr-icon/paths/arrow-cloud-in';
import type { TranslateFunction } from '@repo/phrase';

import { AttachmentDragAndDrop } from './attachment-drag-and-drop.component';
import { AttachmentUploadButton } from './attachment-upload-button.component';
import { DEFAULT_ACCEPT } from './constants';

import styles from './upload-attachment.styles.module.scss';

interface UploadAttachmentTranslations {
    header?: string;
    uploadAction?: string;
}

export interface UploadAttachmentProps {
    onFileSelect: (file: File) => void;
    isDesktop: boolean;
    accept?: string;
    translate: TranslateFunction;
    translations?: UploadAttachmentTranslations;
    disabled?: boolean;
    disabledText?: string;
    loading?: boolean;
}

export const UploadAttachment = ({
    onFileSelect,
    isDesktop,
    accept = DEFAULT_ACCEPT,
    translate,
    translations,
    disabled = false,
    disabledText,
    loading = false,
}: UploadAttachmentProps) => {
    const uploadIcon = <SSRIcon paths={arrowCloudIn} />;
    const fileTypes = accept.replace(/\./g, '').toUpperCase();
    const headerText = translations?.header ?? translate('attachments_upload_header');
    const desktopUploadText =
        disabled && disabledText
            ? disabledText
            : (translations?.uploadAction ?? translate('attachments_upload_drag_and_drop'));
    const mobileUploadText =
        disabled && disabledText
            ? disabledText
            : (translations?.uploadAction ?? translate('attachments_upload_button'));

    return (
        <div className={isDesktop ? styles.attachment_upload_desktop : styles.attachment_upload_mobile}>
            <div className={styles.header_container}>
                <p className={`${styles.header_text}${isDesktop ? '' : ` ${styles.mobile}`}`}>{headerText}</p>
                <p className={`${styles.file_config_text}${isDesktop ? ` ${styles.desktop}` : ''}`}>
                    {translate('attachments_upload_file_config', { fileTypes })}
                </p>
            </div>
            {loading ? (
                <div className={styles.loading_container} data-testid="upload-loading-container">
                    <Loading text={translate('page_loading')}>
                        <LoadingBall color="primary" />
                    </Loading>
                </div>
            ) : isDesktop ? (
                <AttachmentDragAndDrop
                    onFileSelect={onFileSelect}
                    accept={accept}
                    multiple
                    icon={uploadIcon}
                    text={desktopUploadText}
                    disabled={disabled}
                />
            ) : (
                <AttachmentUploadButton
                    onFileSelect={onFileSelect}
                    accept={accept}
                    icon={uploadIcon}
                    buttonText={mobileUploadText}
                    disabled={disabled}
                />
            )}
        </div>
    );
};
